// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import classnames from 'classnames';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {at, compact, find, findIndex, flatten, get, indexOf, isEqual, isFunction, map, orderBy, toString} from 'lodash';

// App Libs
import {formatDate} from '../../lib/core/date';
import formatters from '../../lib/app/formatters';

// Components
import Button from './button';
import AccordionPanel from './accordion-panel';
import AccordionButton from './accordion-button';
import RecordDetails from './record-details';
import SortTitleButton from './sort-title-button';

// Styles
import './table-with-sort.scss';
import {ColoredRow, getRowsColorLogic} from './colorizeRows';

export default class TableWithSort extends Component {
    state = {
        sort: [],
    };

    componentDidMount() {
        const {localSort, sort} = this.props;

        if (!localSort) {
            this.setState({sort});
        }
    }

    componentDidUpdate(nextProps) {
        const {sort: sortState} = this.state;
        const {localSort, sort} = nextProps;

        if (!localSort && !isEqual(sortState, sort)) {
            this.setState({sort});
        }
    }

    getCellClass(searchData, column) {
        const {addClassToCell, className, dataKey} = column;
        let classValue;

        if (addClassToCell && searchData[dataKey]) {
            classValue = `table-with-sort--cell_${this.formatValueToClass(searchData[dataKey])}`;
        }

        return classnames(
            {
                'table-with-sort--cell': true,
                [classValue]: classValue,
            },
            className,
    );
    }

    getClass() {
        const {className, expandable = true, flexLayout} = this.props;

        return classnames(
            {
                'table-with-sort': true,
                'table-with-sort_expandable': expandable,
                'table-with-sort_flex': flexLayout,
            },
            className
        );
    }

    getNextSort = (sortType) => {
        let nextSortType;

        if (!sortType) {
            nextSortType = 'asc';
        } else if (sortType === 'asc') {
            nextSortType = 'desc';
        }

        return nextSortType;
    };

    getRowClass(dataRow) {
        const {activeRowKeys, routerParams} = this.props;
        let rowIsActive = [];

        if (activeRowKeys) {
            rowIsActive = compact(
                map(
                    activeRowKeys,
                    (activeRowKey) =>
                        get(routerParams, activeRowKey) && String(get(dataRow, activeRowKey)) === String(get(routerParams, activeRowKey))
                )
            );
        }

        return classnames({
            'table-with-sort--row': true,
            'table-with-sort_emphasis': this.isRowEmphasis(dataRow),
            'table-with-sort_selected': rowIsActive.length,
        });
    }

    getRowDetailsProps(searchData) {
        const {rowDetails = {}} = this.props;
        const {blacklistKeys, footerRenderer, headerRenderer, keyOrder, whitelistKeys, componentProps} = rowDetails;

        return {
            blacklistKeys,
            className: 'table-with-sort--row-details',
            footerRenderer,
            headerRenderer,
            keyOrder,
            searchData: [searchData],
            whitelistKeys,
            ...componentProps,
        };
    }

    getSortKey(column) {
        const {localSort} = this.props;

        return localSort ? column.dataKey : column.sortKey || column.dataKey;
    }

    getSortType(dataKey) {
        const {sort} = this.state;

        return get(find(sort, {column: dataKey}), 'order');
    }

    getTitleButtonProps(column) {
        const {className, dataKey, maxWidth, title, tooltip, width} = column;
        const sortKey = this.getSortKey(column);

        return {
            className,
            dataKey,
            disabled: !this.isSortable(dataKey),
            key: dataKey,
            maxWidth,
            sortKey,
            sortType: this.getSortType(sortKey),
            onClick: this.setSort,
            title: tooltip || title,
            width,
        };
    }

    setSort = (sortColumn) => {
        const {localSort, setSort} = this.props;

        if (localSort) {
            const {sort} = this.state;
            const sortIndex = findIndex(sort, (sortObj) => sortObj.column === sortColumn);
            const newSort = sort.slice(0);
            const newSortObj = {
                column: sortColumn,
                order: this.getNextSort(get(newSort[sortIndex], 'order')),
            };

            if (sortIndex > -1) {
                newSortObj.order ? newSort.splice(sortIndex, 1, newSortObj) : newSort.splice(sortIndex, 1);
            } else {
                newSort.push(newSortObj);
            }

            this.setState({sort: newSort});
        } else {
            setSort(sortColumn);
        }
    };

    resetSort = () => {
        const {localSort, resetSort} = this.props;

        if (localSort) {
            this.setState({sort: []});
        } else {
            resetSort();
        }
    };

    sorted = (searchData) => {
        const {localSort} = this.props;
        const {sort} = this.state;

        if (localSort) {
            return orderBy(searchData, map(sort, 'column'), map(sort, 'order'));
        }

        return searchData;
    };

    handleResetSortClick = (event) => {
        event && event.preventDefault && event.preventDefault();

        this.resetSort();
    };

    applyFormatter = (node, dataRow, column, formatter) =>
        (formatter
      ? isFunction(formatter)
        ? formatter(dataRow)
        : isFunction(formatters[formatter])
          ? formatters[formatter](node, dataRow, column)
                  : node
      : node);

    formatValueToClass(value) {
        return value
            .toLowerCase()
            .replace(/[^\w\s]/gi, '')
            .split(' ')
            .join('-');
    }

    isRowEmphasis(dataRow) {
        const {
            rowDetails: {rowEmphasis} = {},
        } = this.props;

        return rowEmphasis ? rowEmphasis(dataRow) : false;
    }

    isSortable(dataKey) {
        const {noSort, notSortable = ['selected', 'description', 'locked']} = this.props;

        return dataKey !== '' && !noSort && (notSortable.length ? !(indexOf(notSortable, dataKey) > -1) : true);
    }

    renderBody() {
      const { expandable = true, draggable = false, onDragEnd, rowColorCallback, rowDetails = {}, searchData } = this.props;
      const content = [];

      this.sorted(searchData).map((dataRow, rowKey) => {
        let Component = 'div';
        let expandButton = null;
        const componentProps = {
          key: rowKey,
          className: this.getRowClass(dataRow),
        };

        if (expandable) {
          Component = AccordionPanel;
          expandButton = <AccordionButton className="table-with-sort--expand" />;
          componentProps.detailsRenderer = () => this.renderRowDetails(dataRow);
          componentProps.disableDefaultToggle = true;
          componentProps.recordId = dataRow.echostarId;
        }

        const { isMapping, rowOptions } = rowColorCallback
            ? { isMapping: true, rowOptions: rowColorCallback(dataRow) }
            : getRowsColorLogic(dataRow, rowDetails?.isMapping, componentProps.className);

        const row = (
                  <div className="table-with-sort--row-label">
                      {isMapping ? (
                          <ColoredRow rowOptions={rowOptions}>{this.renderColumns(dataRow)}</ColoredRow>
                      ) : (
                        this.renderColumns(dataRow)
                      )}
                      {expandButton}
                  </div>
        );

        if (draggable) {
          content.push(
                      <Draggable draggableId={rowKey.toString()} index={rowKey} key={componentProps.key}>
                          {provided => (
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                  <Component {...componentProps}>{row}</Component>
                              </div>
                          )}
                      </Draggable>
                  );
        } else {
          content.push(<Component {...componentProps}>{row}</Component>);
        }
      });

      if (draggable) {
        return (
                  <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="DroppableList">
                          {(provided) => (
                              <div ref={provided.innerRef} {...provided.droppableProps}>
                                  <div className="table-with-sort--body">{content}</div>
                                  {provided.placeholder}
                              </div>
                          )}
                      </Droppable>
                  </DragDropContext>
              );
      }
      return <div className="table-with-sort--body">{content}</div>;
    }

    renderBoolean(searchData, column) {
        return <span>{toString(searchData[column.dataKey])}</span>;
    }

    renderCheckbox(searchData, column) {
        const {checked, onChange} = column;
        const inputChecked = isFunction(checked) ? checked(searchData, column) : checked;

        return <input checked={!!inputChecked} type="checkbox" onChange={onChange(searchData)} />;
    }

    renderColumns(dataRow) {
        const {
            columnConfig,
            rowDetails={onClick: () => {}},
        } = this.props;
        const columns = [];

        columnConfig.map((column, key) => {
            const {formatter, maxWidth, renderRowCallback, type, width} = column;
            const renderType = type || 'string';
            const types = {
                boolean: this.renderBoolean,
                checkbox: this.renderCheckbox,
                component: this.renderComponent,
                date: this.renderDate,
                image: this.renderImage,
                link: this.renderLink,
                string: this.renderString,
            };
            const childContent = () => types[renderType](dataRow, column) || null;
            const content = renderRowCallback
                ? renderRowCallback(dataRow, column)
                : this.applyFormatter(childContent(), dataRow, column, formatter);

            columns.push(
                <div
                    className={this.getCellClass(dataRow, column)}
                    key={key}
                    role="presentation"
                    style={{maxWidth, width}}
                    onClick={typeof onClick === 'function' ? (event) => rowDetails(event, dataRow) : undefined}
                >
                    {content}
                </div>
      );
        });

        return columns;
    }

    renderComponent(searchData, column) {
        const Component = column.component;
        const componentProps = isFunction(column.componentProps)
            ? column.componentProps(searchData, column)
            : column.componentProps || {};

        return <Component {...componentProps} />;
    }

    renderDate(searchData, column) {
        const {dataKey, format, parseFormat} = column;

        return formatDate(get(searchData, dataKey), format, parseFormat);
    }

    renderHeader() {
        const {columnConfig} = this.props;
        const headerContent = [];

        map(columnConfig, (column, key) => {
            const {renderTitleCallback} = column;
            const renderContent = renderTitleCallback
                ? renderTitleCallback(column, key)
                : this.renderTitleButton(column, key);

            headerContent.push(renderContent);
        });

        return <header className="table-with-sort--header">{headerContent}</header>;
    }

    renderImage(searchData, column) {
        const {imageAlt, imageKey} = column;

        return <img alt={imageAlt} className="table-with-sort--image" src={searchData[imageKey]} />;
    }

    renderLink(searchData, column) {
        const {dataKey, link, linkKey} = column;
        const toLink = linkKey ? flatten([link, at(searchData, linkKey)]).join('/') : link;

        return (
            <Link className="table-with-sort--link" to={toLink}>
              {get(searchData, dataKey)}
            </Link>
        );
    }

    renderMenu() {
        const {noSort} = this.props;

        return !noSort ? (
            <div className="table-with-sort--menu">
                <Button
                    {...{
                        className: 'table-with-sort--reset-sort-button',
                        icon: 'undo',
                        onClick: this.handleResetSortClick,
                        title: 'Clear all sorts',
                        type: 'tableTitle',
                        value: '',
                    }}
                />
            </div>
        ) : null;
    }

    renderPosition(sortKey) {
        const {sort} = this.state;

        const sortDataIndex = findIndex(sort, {column: sortKey});
        let content = null;

        if (sortDataIndex !== -1) {
            content = <div className="table-with-sort--position">{sortDataIndex + 1}</div>;
        }

        return content;
    }

    renderRowDetails = (searchData) => {
        const {rowDetails = {}} = this.props;
        const {component, renderDetail} = rowDetails;
        let content;

        if (renderDetail) {
            content = renderDetail(this.getRowDetailsProps(searchData));
        }

        if (!content) {
            const Component = component || RecordDetails;

            content = <Component {...this.getRowDetailsProps(searchData)} />;
        }

        return content;
    };

    renderString(searchData, column) {
        return toString(get(searchData, column.dataKey));
    }

    renderTitleButton(column, key) {
        return (
            <SortTitleButton key={key} {...this.getTitleButtonProps(column)}>
                {this.renderPosition(this.getSortKey(column))}
                {column.label ? column.label : column.title}
            </SortTitleButton>
        );
    }

    render() {
        return (
            <div className={this.getClass()}>
                {this.renderHeader()}
                {this.renderMenu()}
                {this.renderBody()}
            </div>
        );
    }
}

TableWithSort.propTypes = {
    activeRow: PropTypes.object,
    activeRowKeys: PropTypes.array,
    className: PropTypes.string,
    columnConfig: PropTypes.arrayOf(PropTypes.object).isRequired,
    draggable: PropTypes.bool,
    expandable: PropTypes.bool,
    flexLayout: PropTypes.bool,
    localSort: PropTypes.bool,
    noSort: PropTypes.bool,
    notSortable: PropTypes.array,
    onDragEnd: PropTypes.func,
    resetSort: PropTypes.func,
    routerParams: PropTypes.object,
    rowColorCallback: PropTypes.func,
    rowDetails: PropTypes.object,
    searchData: PropTypes.arrayOf(PropTypes.object).isRequired,
    setSort: PropTypes.func,
    sort: PropTypes.array,
};
